import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, doc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import QRCode from 'qrcode';
import { Printer, KeyRound, Ban, RotateCw, AlertTriangle } from 'lucide-react';
import { buildPayload, newSecret, qrDocId, cardState, SUBJECT } from '../attendanceQr';
import { useT } from '../i18n.jsx';

/**
 * Issuing, revoking and printing the personal cards that Scan mode reads.
 *
 * ISSUE NEVER OVERWRITES. It only creates cards for people who do not have a working one, so a
 * misclick cannot kill the codes already printed on a class set of student cards. Replacing a code
 * is Reissue, and taking one out of use is Revoke -- both are their own button, both confirm, and
 * both say how many printed cards they are about to break.
 *
 * That separation is the whole point: the destructive action must be the one you had to choose.
 *
 * Expiry is a date the school sets, not the school year. Some schools issue one card for a student's
 * whole time here. It lives in Firestore rather than in the printed code, which means a school can
 * extend or shorten it -- or lift it entirely -- without reprinting anything.
 */

const CHUNK = 400;
const todayIso = () => new Date().toLocaleDateString('en-CA');

const AttendanceCards = ({ activeSchoolId, isMobile, students, staffList, canManage }) => {
  const { t } = useT();
  const [scope, setScope] = useState('class');
  const [cls, setCls] = useState('');
  const [issued, setIssued] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [previews, setPreviews] = useState({});
  const [expiresOn, setExpiresOn] = useState('');

  const today = todayIso();

  const classes = useMemo(
    () => [...new Set(students.map(s => s.currentClass).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [students],
  );
  useEffect(() => { if (!cls && classes.length) setCls(classes[0]); }, [classes, cls]);

  const load = useCallback(async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'attendance_qr'));
      const map = {};
      snap.forEach(d => { map[d.id] = d.data(); });
      setIssued(map);
    } catch (e) {
      console.error('Error loading issued cards', e);
    } finally {
      setLoading(false);
    }
  }, [activeSchoolId]);
  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => {
    if (scope === 'staff') {
      return staffList.map(s => ({
        type: SUBJECT.STAFF, id: String(s.email || '').toLowerCase(),
        name: s.name || s.initial || s.email, line2: s.initial || '',
        extra: { email: String(s.email || '').toLowerCase(), initial: s.initial || '' },
      }));
    }
    return students
      .filter(s => s.currentClass === cls)
      .sort((a, b) => Number(a.classNumber || 0) - Number(b.classNumber || 0))
      .map(s => ({
        type: SUBJECT.STUDENT, id: s.sid,
        name: `${s.name}${s.chineseName ? ` ${s.chineseName}` : ''}`,
        line2: [s.currentClass, s.classNumber].filter(Boolean).join(' · '),
        extra: { email: s.email || '', currentClass: s.currentClass || '', classNumber: s.classNumber || '' },
      }));
  }, [scope, cls, students, staffList]);

  const stateOf = useCallback(
    (p) => cardState(issued[qrDocId(p.type, p.id)], today),
    [issued, today],
  );
  const counts = useMemo(() => people.reduce((acc, p) => {
    acc[stateOf(p)] += 1; return acc;
  }, { none: 0, active: 0, expired: 0, revoked: 0 }), [people, stateOf]);

  const commit = async (targets, build, label) => {
    setWorking(label);
    try {
      for (let i = 0; i < targets.length; i += CHUNK) {
        const batch = writeBatch(db);
        targets.slice(i, i + CHUNK).forEach(p => {
          batch.set(doc(db, 'schools', activeSchoolId, 'attendance_qr', qrDocId(p.type, p.id)),
            build(p), { merge: true });
        });
        await batch.commit();
      }
      await load();
      setPreviews({});
    } catch (e) {
      console.error(`Error during ${label}`, e);
      alert(t('attendance.cards.actionFailed', { message: e.message }));
    }
    setWorking('');
  };

  const freshCard = (p) => ({
    subjectType: p.type,
    ...(p.type === SUBJECT.STUDENT ? { sid: p.id } : { email: p.id }),
    ...p.extra,
    name: p.name,
    secret: newSecret(),
    expiresOn: expiresOn || '',
    issuedAt: new Date().toISOString(),
    // Cleared explicitly: a reissue after a revoke has to bring the card back, and a merge that
    // left the old flag in place would produce a card that scans as revoked the day it is printed.
    revokedAt: '',
  });

  // Only the people who have nothing usable. Safe by construction: whatever this does, no card that
  // already works stops working.
  const issueMissing = () => {
    const targets = people.filter(p => stateOf(p) !== 'active');
    if (targets.length === 0) return;
    commit(targets, freshCard, 'issue');
  };

  const reissue = () => {
    const live = people.filter(p => stateOf(p) === 'active').length;
    if (!window.confirm(t('attendance.cards.reissueConfirm', { total: people.length, live }))) return;
    commit(people, freshCard, 'reissue');
  };

  const revokeAll = () => {
    const targets = people.filter(p => stateOf(p) === 'active');
    if (targets.length === 0) return;
    if (!window.confirm(t('attendance.cards.revokeAllConfirm', { count: targets.length }))) return;
    commit(targets, () => ({ revokedAt: new Date().toISOString() }), 'revoke');
  };

  const revokeOne = (p) => {
    if (!window.confirm(t('attendance.cards.revokeOneConfirm', { name: p.name }))) return;
    commit([p], () => ({ revokedAt: new Date().toISOString() }), 'revoke');
  };

  const setExpiryOnActive = () => {
    const targets = people.filter(p => stateOf(p) !== 'none');
    if (targets.length === 0) return;
    if (!window.confirm(expiresOn
      ? t('attendance.cards.setExpiryConfirm', { count: targets.length, date: expiresOn })
      : t('attendance.cards.clearExpiryConfirm', { count: targets.length }))) return;
    commit(targets, () => ({ expiresOn: expiresOn || '' }), 'expiry');
  };

  const print = async () => {
    const ready = people.filter(p => stateOf(p) === 'active');
    if (ready.length === 0) { alert(t('attendance.cards.nothingToPrint')); return; }
    setWorking('print');
    try {
      const made = {};
      for (const p of ready) {
        const rec = issued[qrDocId(p.type, p.id)];
        made[p.id] = await QRCode.toDataURL(buildPayload({
          schoolId: activeSchoolId, subjectType: p.type, id: p.id, secret: rec.secret,
        }), { width: 320, margin: 1 });
      }
      setPreviews(made);
      // Painted before the print dialog opens; without this the sheet prints blank the first time.
      setTimeout(() => window.print(), 250);
    } catch (e) {
      console.error('Error building the sheet', e);
      alert(t('attendance.cards.printFailed', { message: e.message }));
    }
    setWorking('');
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  };
  const chip = (on) => ({
    padding: '0.28rem 0.75rem', borderRadius: 'var(--radius-md)', fontSize: '0.82rem',
    fontWeight: on ? '700' : '500', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
    backgroundColor: on ? 'var(--color-nav-active)' : 'transparent',
    border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
    color: on ? 'var(--color-text-main)' : 'var(--color-text-subtle)',
  });
  const STATE = {
    active: { label: t('attendance.cards.states.active'), colour: 'var(--color-success-text)' },
    expired: { label: t('attendance.cards.states.expired'), colour: 'var(--color-warning-text)' },
    revoked: { label: t('attendance.cards.states.revoked'), colour: 'var(--color-danger-text)' },
    none: { label: t('attendance.cards.states.none'), colour: 'var(--color-text-muted)' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Only the sheet prints. Everything else on the page is chrome that would waste paper. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #card-sheet, #card-sheet * { visibility: visible !important; }
          #card-sheet { position: absolute; inset: 0; padding: 0; }
          .card-cell { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div style={{ ...card, padding: isMobile ? '0.85rem' : '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {[['class', t('attendance.cards.byClass')], ['staff', t('attendance.cards.staff')]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => { setScope(id); setPreviews({}); }} style={chip(scope === id)}>{label}</button>
          ))}
          {scope === 'class' && (
            <select className="input-field" value={cls} onChange={e => { setCls(e.target.value); setPreviews({}); }}
                    style={{ margin: 0, width: 'auto', minWidth: '130px' }}>
              {classes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
            {loading ? t('common.loading')
              : t('attendance.cards.counts', counts)}
          </span>
        </div>

        {canManage ? (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{t('attendance.cards.expiresOn')}</label>
              <input type="date" className="input-field" value={expiresOn}
                     onChange={e => setExpiresOn(e.target.value)}
                     style={{ margin: 0, width: 'auto' }} />
              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                {expiresOn ? t('attendance.cards.expiryApplied') : t('attendance.cards.expiryBlank')}
              </span>
              <button className="btn-outline" disabled={!!working || counts.none === people.length}
                      onClick={setExpiryOnActive}
                      title={t('attendance.cards.applyToExistingTitle')}
                      style={{ width: 'auto', padding: '0.45rem 0.9rem', fontSize: '0.83rem' }}>
                {t('attendance.cards.applyToExisting')}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Safe. Only touches people with nothing that works, so a misclick costs nothing. */}
              <button className="btn-primary" disabled={!!working || counts.none + counts.expired + counts.revoked === 0}
                      onClick={issueMissing} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                <KeyRound size={15} /> {t('attendance.cards.issueMissing', { count: counts.none + counts.expired + counts.revoked })}
              </button>
              <button className="btn-outline" disabled={!!working || counts.active === 0} onClick={print}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                <Printer size={15} /> {working === 'print' ? t('attendance.cards.building') : t('attendance.cards.print', { count: counts.active })}
              </button>

              {/* The two that break printed cards, kept apart from the two that do not, and coloured
                  like what they are. */}
              <span style={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'var(--color-border)', margin: '0 0.3rem' }} />
              <button className="btn-outline" disabled={!!working} onClick={reissue}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem', borderColor: 'var(--color-warning-text)', color: 'var(--color-warning-text)' }}>
                <RotateCw size={15} /> {t('attendance.cards.reissueAll', { count: people.length })}
              </button>
              <button className="btn-outline" disabled={!!working || counts.active === 0} onClick={revokeAll}
                      style={{ width: 'auto', padding: '0.55rem 1.1rem', borderColor: 'var(--color-danger)', color: 'var(--color-danger-text)' }}>
                <Ban size={15} /> {t('attendance.cards.revokeActive', { count: counts.active })}
              </button>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>
            {t('attendance.cards.readOnly')}
          </p>
        )}

        {(counts.expired > 0 || counts.revoked > 0) && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', fontSize: '0.82rem', lineHeight: 1.6 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
            <span>
              {counts.expired > 0 && `${t('attendance.cards.warnExpired', { count: counts.expired })} `}
              {counts.revoked > 0 && `${t('attendance.cards.warnRevoked', { count: counts.revoked })} `}
              {t('attendance.cards.warnTail')}
            </span>
          </div>
        )}
      </div>

      <div style={{ ...card, overflow: 'hidden' }}>
        {people.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
            {t('attendance.cards.nobody')}
          </div>
        ) : people.map(p => {
          const st = stateOf(p);
          const rec = issued[qrDocId(p.type, p.id)];
          return (
            <div key={p.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.5rem 0.95rem', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600' }}>{p.name}</span>
                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  {[p.line2, p.id, rec?.expiresOn ? t('attendance.cards.expiresLabel', { date: rec.expiresOn }) : (rec ? t('attendance.cards.noExpiry') : '')].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span style={{ fontSize: '0.7rem', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0, color: STATE[st].colour }}>
                {STATE[st].label}
              </span>
              {canManage && st === 'active' && (
                <button onClick={() => revokeOne(p)} title={t('attendance.cards.revokeOneTitle', { name: p.name })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex', padding: '0.25rem', flexShrink: 0 }}>
                  <Ban size={15} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div id="card-sheet" style={{ display: Object.keys(previews).length ? 'grid' : 'none', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.5rem' }}>
        {people.filter(p => previews[p.id]).map(p => (
          <div key={p.id} className="card-cell" style={{ border: '1px solid #999', borderRadius: '6px', padding: '0.5rem', textAlign: 'center', background: '#fff', color: '#000' }}>
            <img src={previews[p.id]} alt="" style={{ width: '100%', maxWidth: '150px' }} />
            <div style={{ fontSize: '0.78rem', fontWeight: 700 }}>{p.name}</div>
            <div style={{ fontSize: '0.7rem' }}>{p.line2}</div>
            <div style={{ fontSize: '0.65rem', fontFamily: 'monospace' }}>{p.id}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceCards;
